/**
 * Insights - narrative cards.
 *
 * Design choices (senior UI/UX perspective):
 *   • No horizontal scroll - scroll on a single row is a code smell on
 *     desktop and hides content behind affordances the user has to
 *     discover. Cards live in a responsive grid (1/2/3 columns) so every
 *     insight is glanceable at once.
 *   • Featured card - the first/most relevant insight gets a wider, taller
 *     treatment with a deeper accent ring; the rest sit in a compact grid.
 *     This gives the section a focal point and a clear reading order.
 *   • Sentiment carries only through a soft tinted-icon chip + a left
 *     accent bar - no full-bleed color washes that would compete with the
 *     surrounding charts.
 *   • Hover lifts subtly (border + shadow) - invites attention without
 *     suggesting the card is a button.
 *
 * v2 will swap the rule-based feed for an LLM `GET /me/insights` call;
 * the visual contract stays the same.
 */

import { useState } from 'react';
import { Lightbulb, TrendingUp, AlertCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { InsightSentiment, ProgressInsight } from '@/types/progress';
import { Button, Card, IconMedallion, Skeleton } from '@ai-tutor/ui';
import { ErrorState } from '@/components/core/common/boundaries/ErrorState';

// Types moved to `@/types/progress` so hooks and mock data can use them
// without importing from a component module (an upward hooks -> components
// edge). Re-exported here because 3 call sites already import them from this
// path and the component remains their primary consumer.
export type { InsightSentiment, ProgressInsight } from '@/types/progress';

import { SectionLabel } from './SectionLabel';

interface SentimentStyle {
  accent: string; // border-left color
  iconWrap: string; // tinted chip background
  iconColor: string; // icon stroke color
  icon: LucideIcon;
}

const SENTIMENT_STYLES: Record<InsightSentiment, SentimentStyle> = {
  positive: {
    accent: 'before:bg-brand-500 dark:before:bg-primary',
    iconWrap: 'bg-brand-50 dark:bg-primary/40',
    iconColor: 'text-brand-500 dark:text-primary',
    icon: TrendingUp,
  },
  // Light keeps distinct sentiment hues; dark unifies to the lavender --primary
  // (the icon already carries the sentiment) so cards stay on-brand. A slightly
  // different chip opacity per sentiment keeps a faint distinction.
  neutral: {
    accent: 'before:bg-sky-400 dark:before:bg-primary',
    iconWrap: 'bg-sky-50 dark:bg-primary/20',
    iconColor: 'text-sky-500 dark:text-primary',
    icon: Lightbulb,
  },
  attention: {
    accent: 'before:bg-violet-500 dark:before:bg-primary',
    iconWrap: 'bg-violet-50 dark:bg-primary/30',
    iconColor: 'text-violet-500 dark:text-primary',
    icon: AlertCircle,
  },
};

function InsightCard({ insight }: { insight: ProgressInsight }) {
  const style = SENTIMENT_STYLES[insight.sentiment ?? 'neutral'];
  const Icon = style.icon;
  return (
    <Card
      elevation='none'
      className={[
        'group relative overflow-hidden p-4 transition-all duration-200',
        // `border-border` on hover, not `border-gray-300/80`. The raw grey was
        // a light-mode-only guess that sat beside a `dark:` token override on
        // the same property — the palette-in-light/tokens-in-dark split this
        // codebase has been retiring.
        'hover:border-border hover:shadow-sm',
        // Left accent bar via pseudo-element so we don't double up borders.
        'before:absolute before:left-0 before:top-0 before:h-full before:w-1',
        style.accent,
      ].join(' ')}
    >
      <div className='flex gap-3'>
        <IconMedallion size='sm' className={style.iconWrap}>
          <Icon className={`h-4 w-4 ${style.iconColor}`} />
        </IconMedallion>
        <div className='min-w-0 flex-1'>
          {insight.title && (
            <div className='mb-1 text-xs font-semibold text-muted-foreground'>
              {insight.title}
            </div>
          )}
          <p className='text-sm leading-snug text-foreground'>{insight.body}</p>
        </div>
      </div>
    </Card>
  );
}

function CardSkeleton() {
  return (
    <Card elevation='none' className='relative overflow-hidden p-4'>
      <span className='absolute left-0 top-0 h-full w-1 bg-secondary' />
      <div className='flex gap-3'>
        <div className='h-8 w-8 shrink-0 rounded-lg bg-secondary' />
        <div className='min-w-0 flex-1 space-y-2'>
          <Skeleton className='h-3 w-24' />
          <Skeleton className='h-3 w-full' />
          <Skeleton className='h-3 w-4/5' />
        </div>
      </div>
    </Card>
  );
}

export function InsightsSection({
  insights,
  isLoading,
  loadFailed,
  onRetry,
  initialVisible,
}: {
  insights?: ProgressInsight[];
  isLoading?: boolean;
  /**
   * The read FAILED with nothing cached. Renders a retryable error in place
   * of "Nothing to highlight yet", which would otherwise tell the user they
   * have no insights when the request never succeeded.
   */
  loadFailed?: boolean;
  onRetry?: () => void;
  /**
   * Show this many cards and a "Show N more" button for the rest. The phone
   * passes 2: there the grid is one column, and all of them stacked was a
   * screen and a half of prose before the next section. Unset shows all.
   */
  initialVisible?: number;
}) {
  const [expanded, setExpanded] = useState(false);

  if (isLoading) {
    return (
      <div className='space-y-3'>
        <SectionLabel>Insights</SectionLabel>
        <div className='grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3'>
          {Array.from({ length: initialVisible ?? 6 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className='space-y-3'>
        <SectionLabel>Insights</SectionLabel>
        <ErrorState
          variant='inline'
          title="Couldn't load insights"
          description='Your study history is safe.'
          primaryAction={
            onRetry ? { label: 'Retry', onClick: onRetry } : undefined
          }
        />
      </div>
    );
  }

  if (!insights || insights.length === 0) {
    // Don't disappear silently - render a single full-width placeholder
    // card so the user knows the section exists and what fills it later.
    return (
      <div className='space-y-3'>
        <SectionLabel>Insights</SectionLabel>
        <Card elevation='none' className='p-5'>
          <div className='mb-1 text-xs font-semibold text-muted-foreground'>
            Nothing to highlight yet
          </div>
          <p className='text-sm leading-snug text-muted-foreground'>
            Insights appear as your study history grows. Open a lesson or
            attempt a quiz and they'll start showing up here.
          </p>
        </Card>
      </div>
    );
  }

  // Cap at 6 - fits cleanly in 3 cols × 2 rows on lg, 2 cols × 3 rows on md.
  const all = insights.slice(0, 6);
  const visible =
    initialVisible && !expanded ? all.slice(0, initialVisible) : all;
  const extra = initialVisible ? Math.max(0, all.length - initialVisible) : 0;

  return (
    <div className='space-y-3'>
      <SectionLabel>Insights</SectionLabel>
      <div className='grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3'>
        {visible.map((insight, idx) => (
          <InsightCard
            key={`${insight.title ?? ''}|${insight.body}|${idx}`}
            insight={insight}
          />
        ))}
      </div>
      {/* A toggle rather than a one-way reveal: the button stays where the
          finger is, so focus is never dropped on the floor, and the list can
          be folded again before scrolling on. */}
      {extra > 0 && (
        <Button
          variant='outline'
          className='h-11 w-full'
          aria-expanded={expanded}
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? 'Show fewer' : `Show ${extra} more`}
        </Button>
      )}
    </div>
  );
}
